import { ref } from 'vue';
import { useSubscriptionStore } from '../stores/subscription';
import type { StripePlan } from '../types';
import { logger } from '@maso-dj/shared-types';

/**
 * Composable for managing subscription plans and checkout
 * Plans are cached in Pinia store to persist across component lifecycles
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useSubscriptionPlans() {
  const subscriptionStore = useSubscriptionStore();

  const plans = ref<StripePlan[]>([]);
  const processingPriceId = ref<string | null>(null);
  const loadingPlans = ref(false);
  const error = ref<string | null>(null);

  /**
   * Load available subscription plans from Stripe
   * Plans are cached in Pinia store to avoid repeated API calls
   * @param subscriptionsOnly - If true, only show recurring subscriptions (exclude one-time payments)
   */
  async function loadPlans(subscriptionsOnly = false): Promise<void> {
    loadingPlans.value = true;
    error.value = null;

    try {
      // Fetch plans from store (store handles caching)
      const response = await subscriptionStore.fetchPlans();

      // Filter based on mode
      if (subscriptionsOnly) {
        // Filter to only show subscription plans (exclude one-time payment plans)
        // Used for conversion dialogs where users upgrade from one-time to recurring
        plans.value = response.plans.filter((plan: StripePlan) => plan.mode === 'subscription');
      } else {
        // Show all plans (used for pricing page where all options should be available)
        plans.value = response.plans;
      }
    } catch {
      error.value = 'Failed to load subscription plans. Please try again later.';
    } finally {
      loadingPlans.value = false;
    }
  }

  /**
   * Create a Stripe checkout session and redirect to checkout
   * @param priceId - The Stripe price ID to subscribe to
   */
  async function createCheckoutSession(priceId: string): Promise<void> {
    processingPriceId.value = priceId;
    error.value = null;

    try {
      const checkoutUrl = await subscriptionStore.createCheckoutSession(priceId);

      if (checkoutUrl) {
        // Redirect to Stripe Checkout (processingPriceId stays set until redirect completes)
        window.location.href = checkoutUrl;
      } else {
        error.value = 'Failed to create checkout session. Please try again.';
        processingPriceId.value = null;
      }
    } catch (err) {
      logger.error('Subscription error:', err);

      // Differentiate error types
      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          error.value = 'Authentication required. Please log in and try again.';
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          error.value = 'Network error. Please check your connection and try again.';
        } else {
          error.value = err.message;
        }
      } else {
        error.value = 'An unexpected error occurred. Please try again.';
      }

      processingPriceId.value = null;

      // Clear any pending checkout state on error
      sessionStorage.removeItem('pendingPriceId');
      sessionStorage.removeItem('pendingCheckoutSource');
    }
  }

  /**
   * Clear the cached plans and force a fresh fetch on next loadPlans() call
   * Useful for testing or when plans are updated
   */
  function clearCache(): void {
    logger.debug('[useSubscriptionPlans] Clearing plans cache');
    subscriptionStore.clearPlansCache();
  }

  return {
    plans,
    processingPriceId,
    loadingPlans,
    error,
    loadPlans,
    createCheckoutSession,
    clearCache,
  };
}
